/**
 * Route Ordering Clarity - Shared Utilities
 *
 * These utilities are used by both backend and frontend to ensure
 * identical routing key calculations for change detection.
 *
 * @see docs/features/ROUTE_ORDERING_CLARITY.md
 */

import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';

/**
 * Input data for routing key calculation
 * Note: userId and date are NOT included - they are selection criteria only
 */
export interface RoutingKeyWorkorder {
  recnum: number;
  location_address: string | null;
  route_option: 'direct' | 'via_hq' | string | null;
}

/**
 * Normalize address for comparison
 * Removes formatting variations that don't affect routing.
 * Keeps postal codes as they help identify exact locations in the Netherlands.
 *
 * @param address - Raw address string (may contain newlines, etc.)
 * @returns Normalized address string for comparison
 */
export function normalizeAddress(address: string | null | undefined): string {
  if (!address) return '';

  return address.toLowerCase().replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Calculate deterministic routing key for change detection
 *
 * The routing key is a SHA-256 hash of the workorder data that affects routing:
 * - Workorder identifiers (recnum)
 * - Location addresses (normalized)
 * - Route options (direct/via_hq)
 *
 * IMPORTANT: userId and date are NOT included because they are selection
 * criteria, not routing inputs. If you reassign all workorders to a different
 * user/day, the route itself doesn't change.
 *
 * @param workorders - Array of workorders to hash
 * @returns SHA-256 hash as 64-character hex string
 */
export function calculateRoutingKey(workorders: RoutingKeyWorkorder[]): string {
  // Sort workorders by recnum for deterministic ordering
  const sortedWorkorders = [...workorders]
    .sort((a, b) => a.recnum - b.recnum)
    .map((wo) => ({
      recnum: wo.recnum,
      // Normalize address for comparison
      address: normalizeAddress(wo.location_address),
      route_option: wo.route_option ?? 'direct',
    }));

  const payload = {
    workorders: sortedWorkorders,
  };

  // Create stable JSON representation
  const jsonString = JSON.stringify(payload);

  // Return SHA-256 hash
  // Note: This uses the Web Crypto API which works in both browser and Node.js 18+
  return sha256Hex(jsonString);
}

/**
 * Compute SHA-256 hash as hex string
 * Uses @noble/hashes for cross-platform sync support
 */
function sha256Hex(input: string): string {
  const encoder = new TextEncoder();
  return bytesToHex(sha256(encoder.encode(input)));
}

/**
 * Route status determined from routing metadata
 */
export type RouteStatus =
  | 'unsorted' // sort_order_source = NULL
  | 'auto_valid' // sort_order_source = 'auto' + keys match
  | 'auto_stale' // sort_order_source = 'auto' + keys differ
  | 'manual_valid' // sort_order_source = 'manual' + keys match
  | 'manual_stale'; // sort_order_source = 'manual' + keys differ

/**
 * Determine the route status from workorder metadata
 *
 * @param sortOrderSource - The sort_order_source from database
 * @param storedKey - The routing_key from database
 * @param currentKey - Calculated routing key from current workorder data
 * @returns Route status enum value
 */
export function getRouteStatus(
  sortOrderSource: 'auto' | 'manual' | null | undefined,
  storedKey: string | null | undefined,
  currentKey: string,
): RouteStatus {
  if (sortOrderSource === null || sortOrderSource === undefined) {
    return 'unsorted';
  }

  const keysMatch = storedKey === currentKey;

  if (sortOrderSource === 'auto') {
    return keysMatch ? 'auto_valid' : 'auto_stale';
  }

  // sortOrderSource === 'manual'
  return keysMatch ? 'manual_valid' : 'manual_stale';
}

/**
 * Check if a user can auto-sort a route based on its current status
 *
 * Non-admin users can auto-sort when:
 * - Route is unsorted (NULL sort_order_source)
 * - Route is auto-sorted but has stale/invalid key
 *
 * @param status - Current route status
 * @param isAdmin - Whether the user is an admin
 * @returns Whether the user can trigger auto-sort
 */
export function canAutoSort(status: RouteStatus, isAdmin: boolean): boolean {
  // Admins can always auto-sort
  if (isAdmin) return true;

  // Non-admins can auto-sort unsorted or stale auto routes
  return status === 'unsorted' || status === 'auto_stale';
}

/**
 * Check if a user can make manual route changes
 *
 * Only admins can make manual changes (drag-drop, set times, etc.)
 *
 * @param isAdmin - Whether the user is an admin
 * @returns Whether the user can make manual changes
 */
export function canManualEdit(isAdmin: boolean): boolean {
  return isAdmin;
}
